import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/api/axios';
import { Loader2, Search, Trash2, Upload, ShieldCheck } from 'lucide-react';

export const MetaDataDeletion = () => {
  const [idsText, setIdsText] = useState('');
  const [scan, setScan] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');

  const leerArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setIdsText((t) => (t ? t + '\n' : '') + String(reader.result || ''));
      toast.success(`Archivo "${file.name}" cargado`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const analizar = async () => {
    try {
      setBusy('scan');
      setReport(null);
      const { data } = await api.post('/admin/meta-deletion/scan', { ids_text: idsText });
      setScan(data);
      if (!data.encontrados.length) toast.info('Ningún ID de la lista tiene datos en el CRM. No hay nada que eliminar.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error analizando'); }
    finally { setBusy(''); }
  };

  const eliminar = async () => {
    const n = scan?.encontrados?.length || 0;
    if (!window.confirm(`⚠️ Se eliminarán DEFINITIVAMENTE los datos de ${n} cliente(s): conversaciones, mensajes y pedidos. Esta acción no se puede deshacer. ¿Continuar?`)) return;
    try {
      setBusy('del');
      const { data } = await api.post('/admin/meta-deletion/execute', { ids_text: idsText });
      setReport(data);
      setScan(null);
      setIdsText('');
      toast.success('Datos eliminados. Solicitud de Meta cumplida.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error eliminando'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="meta-deletion-tab">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-400" /> Eliminación de datos solicitada por Meta</h2>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Cuando Meta te envíe un correo de "Solicitud de eliminación de datos de usuarios": descarga el archivo de identificadores desde
          developers.facebook.com → tu app → Configuración → Avanzada → "Descargar identificadores de usuario", súbelo aquí (o pega los IDs) y elimina sus datos del CRM.
        </p>
      </div>

      <div className="space-y-2">
        <textarea value={idsText} onChange={(e) => setIdsText(e.target.value)} rows={5} data-testid="meta-del-ids-input"
          placeholder="Pega aquí los identificadores de usuario del archivo de Meta (en cualquier formato: uno por línea, CSV, JSON…)"
          className="w-full bg-transparent border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-xs font-mono outline-none resize-y" />
        <div className="flex gap-2 flex-wrap">
          <label data-testid="meta-del-file-btn" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-500/10 cursor-pointer transition-colors">
            <Upload className="w-3.5 h-3.5" /> Subir archivo de Meta
            <input type="file" accept=".txt,.csv,.json,text/plain,text/csv,application/json" className="hidden" onChange={leerArchivo} data-testid="meta-del-file-input" />
          </label>
          <button onClick={analizar} disabled={busy !== '' || !idsText.trim()} data-testid="meta-del-scan-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white transition-colors">
            {busy === 'scan' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} 1. Analizar
          </button>
        </div>
      </div>

      {scan && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3" data-testid="meta-del-scan-result">
          <p className="text-sm font-bold">
            Se leyeron <span className="text-blue-400">{scan.total_ids}</span> IDs · Con datos en el CRM: <span className="text-red-400">{scan.encontrados.length}</span> · Sin datos (se ignoran): <span className="opacity-60">{scan.no_encontrados}</span>
          </p>
          {scan.encontrados.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left opacity-60 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-1.5 pr-3">Cliente</th><th className="pr-3">Plataforma</th><th className="pr-3">ID Meta</th>
                    <th className="pr-3">Conversaciones</th><th className="pr-3">Mensajes</th><th>Pedidos</th>
                  </tr></thead>
                  <tbody>
                    {scan.encontrados.map((m) => (
                      <tr key={m.psid} className="border-b border-slate-100 dark:border-slate-800" data-testid={`meta-del-row-${m.psid}`}>
                        <td className="py-1.5 pr-3 font-bold">{m.nombre}</td>
                        <td className="pr-3">{m.plataforma}</td>
                        <td className="pr-3 font-mono opacity-70">{m.psid}</td>
                        <td className="pr-3">{m.conversaciones}</td>
                        <td className="pr-3">{m.mensajes}</td>
                        <td>{m.pedidos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={eliminar} disabled={busy !== ''} data-testid="meta-del-execute-btn"
                className="inline-flex items-center gap-1.5 text-xs font-black px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white transition-colors">
                {busy === 'del' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} 2. Eliminar definitivamente
              </button>
            </>
          )}
        </div>
      )}

      {report && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-xl p-4" data-testid="meta-del-report">
          <p className="font-black text-sm text-emerald-500">✅ Eliminación completada — solicitud de Meta cumplida</p>
          <ul className="text-xs mt-2 space-y-1">
            <li>IDs procesados: <b>{report.total_ids}</b></li>
            <li>Clientes eliminados: <b>{report.clientes_eliminados}</b></li>
            <li>Conversaciones eliminadas: <b>{report.conversaciones_eliminadas}</b></li>
            <li>Mensajes eliminados: <b>{report.mensajes_eliminados}</b></li>
            <li>Pedidos eliminados: <b>{report.pedidos_eliminados}</b></li>
            <li className="opacity-60">IDs sin datos en el CRM (ignorados, como indica Meta): {report.sin_datos}</li>
          </ul>
          <p className="text-[11px] opacity-60 mt-2">Queda registro de auditoría de esta eliminación. No necesitas responder el correo de Meta: con eliminar los datos es suficiente.</p>
        </div>
      )}
    </div>
  );
};
